import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { withTheme } from "@mui/styles";
import { mean, round } from "mathjs";
import PropTypes from "prop-types";
import React, { Component, memo, useContext, useMemo, useState } from "react";
import ReactStars from "react-stars";
import { rrulestr } from "rrule";

import { Block, Edit, Phone } from "@mui/icons-material";
import _ from "lodash";
import { DateTime } from "luxon";
import TagManager from "react-gtm-module";
import Slider from "react-slick";
import MapContext from "../../Contexts/MapContext";
import UserContext, { RESOURCES } from "../../Contexts/UserContext";
import {
  getDeliveryEntitiesForStores,
  getProductsForStores,
} from "../../Services/Data.service";
import { getStoreName, validateSchedule } from "../../Utils/Utils";
import StoreActionButtons from "../Components/StoreActionButtons";

const ContactInfo = memo(({ item, deliveryEntities }) => {
  const { deliveryInventory } = useContext(MapContext);
  const storeInventory = deliveryInventory?.filter((i) =>
    (deliveryEntities || [])
      .map((de) => de.delivery_entity_uid)
      .includes(i.delivery_entity_uid),
  );
  const deliveryEntityPhone = storeInventory?.find(
    (i) => !!i?.delivery_entity?.phone,
  )?.delivery_entity?.phone;
  const phone = item.phone || deliveryEntityPhone;
  return (
    <>
      {phone && (
        <Button
          href={"tel:" + phone}
          variant="text"
          style={{ justifyContent: "flex-start" }}
          color="inherit"
          startIcon={<Phone />}
        >
          {phone}
        </Button>
      )}
    </>
  );
});

const getProductImage = (item, storeChain) => {
  const cleanChain = `${storeChain}`.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const productImageForChain = item?.chain_images?.[cleanChain];
  const imageUrl = productImageForChain || item?.url;
  return imageUrl;
};

const SliderCard = memo(
  ({
    productItem,
    index,
    sliderIndex,
    productsLength,
    overrideImage,
    storeChain,
    userContext,
    storeLocatorConfig,
    mapProducts,
  }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const product = mapProducts?.find((p) => p.id === productItem?.product_id);
    return (
      <div style={{ width: productsLength > 1 ? 150 : undefined }}>
        <div style={{ textAlign: "center" }}>
          {imageFailed ||
          !(overrideImage || getProductImage(product, storeChain)) ? (
            <Typography
              color={"tomato"}
              border={"1px solid tomato"}
              borderRadius={1}
              p={1}
              bgcolor="whitesmoke"
              width="100%"
              height={150}
            >
              No Image
            </Typography>
          ) : (
            <img
              alt="product"
              src={overrideImage || getProductImage(product, storeChain)}
              width="100%"
              height={150}
              draggable={false}
              style={{
                margin: "auto",
                maxHeight: 250,
                objectFit: "contain",
                padding: 10,
              }}
              onError={() => setImageFailed(true)}
            />
          )}
          {index === sliderIndex && (
            <div
              style={{
                textAlign: "center",
                width: productsLength > 1 ? 250 : undefined,
                position: productsLength > 1 ? "relative" : undefined,
                left: productsLength > 1 ? -50 : undefined,
              }}
            >
              <Typography variant="body2">
                {userContext?.formatText(product?.name)}
              </Typography>
              <Typography variant="caption">
                {product?.description || product?.category || product?.flavor}
              </Typography>
              {productItem?.price && (
                <Typography variant="caption">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 2,
                  }).format(productItem?.price_oz || productItem?.price) +
                    (productItem?.price_oz ? " / oz" : "")}
                </Typography>
              )}
              {storeLocatorConfig.storeDetails?.showProductReviews && (
                <div>
                  <ReactStars
                    count={5}
                    edit={false}
                    value={productItem?.score_num || -1}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

const GridCard = memo(
  ({ productItem, overrideImage, storeChain, userContext, mapProducts }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const product = mapProducts?.find((p) => p.id === productItem.product_id);
    return (
      <Paper
        style={{
          overflow: "hidden",
          height: "100%",
        }}
      >
        <Stack
          direction={"column"}
          alignItems={"center"}
          spacing={1}
          p={1}
          height={"100%"}
        >
          {imageFailed ||
          !(overrideImage || getProductImage(product, storeChain)) ? (
            <Typography
              color={"tomato"}
              border={"1px solid tomato"}
              borderRadius={1}
              p={1}
              bgcolor="whitesmoke"
            >
              No Image
            </Typography>
          ) : (
            <img
              alt="product"
              src={overrideImage || getProductImage(product, storeChain)}
              width={80}
              style={{ maxHeight: 250, objectFit: "contain" }}
              onError={() => setImageFailed(true)}
            />
          )}
          <Typography variant="body2">
            {userContext?.formatText(product?.name)}
          </Typography>
          {!!product?.category && (
            <Typography variant="caption" alignContent={"end"} flex={1}>
              {userContext?.formatText(product?.category)}
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  },
);

const CustomSliderPaging = memo(
  ({ product, index, sliderIndex, overrideImage, storeChain, classes }) => {
    const [imageFailed, setImageFailed] = useState(false);
    return !imageFailed &&
      (overrideImage || getProductImage(product, storeChain)) ? (
      <img
        alt={product?.name}
        src={overrideImage || getProductImage(product, storeChain)}
        height={20}
        width={20}
        style={{
          objectFit: "contain",
          border:
            sliderIndex === index
              ? `solid 1px ${classes?.palette?.primary?.[400]}`
              : undefined,
        }}
        onError={() => setImageFailed(true)}
      />
    ) : (
      <Block
        style={{
          height: 20,
          width: 20,
          backgroundColor: "whitesmoke",
          borderRadius: 10,
          color: "tomato",
          border:
            sliderIndex === index
              ? `solid 1px ${classes?.palette?.primary?.[400]}`
              : undefined,
        }}
      />
    );
  },
);

const Products = memo(
  ({
    userContext,
    products,
    storeLocatorConfig,
    overrideImage,
    storeChain,
    classes,
  }) => {
    const mapContext = useContext(MapContext);
    const [sliderIndex, setSliderIndex] = useState(1);
    const sliderRef = React.useRef(null);

    const productToShow = useMemo(
      () =>
        products?.filter(
          (productItem) =>
            !!mapContext.products?.find(
              (p) => p.id === productItem?.product_id,
            ),
        ),
      [products, mapContext.products],
    );

    if (productToShow?.length) {
      return (
        <>
          {storeLocatorConfig.storeDetails?.productsVisualization ===
            "grid" && (
            <Grid
              container
              spacing={2}
              maxHeight={"20rem"}
              overflow={"auto"}
              marginTop={2}
            >
              {productToShow?.map((productItem) => (
                <Grid key={productItem.product_id} xs={4}>
                  <GridCard
                    productItem={productItem}
                    overrideImage={overrideImage}
                    storeChain={storeChain}
                    userContext={userContext}
                    mapProducts={mapContext.products}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          {!storeLocatorConfig.storeDetails?.productsVisualization &&
            (productToShow?.length > 1 ? (
              <Slider
                ref={sliderRef}
                dots
                arrows
                centerMode
                autoplay
                pauseOnHover
                centerPadding="0px"
                variableWidth
                initialSlide={1}
                slidesToShow={1}
                slidesToScroll={1}
                afterChange={setSliderIndex}
                appendDots={(dots) => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      overflow: "auto",
                      position: "relative",
                      bottom: "unset",
                    }}
                  >
                    {productToShow?.map((productItem, index) => {
                      const product = mapContext.products?.find(
                        (p) => p.id === productItem?.product_id,
                      );
                      return (
                        <div
                          key={productItem?.product_id}
                          style={{
                            margin: "0 5px",
                            padding: "2px",
                            position: "relative",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              sliderRef.current?.slickGoTo(index);
                            }}
                            style={{
                              cursor: "pointer",
                              border: "none",
                              background: "transparent",
                              outline: "none",
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                            }}
                          ></button>
                          <CustomSliderPaging
                            product={product}
                            index={index}
                            sliderIndex={sliderIndex}
                            overrideImage={overrideImage}
                            storeChain={storeChain}
                            classes={classes}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              >
                {productToShow.map((productItem, index) => (
                  <SliderCard
                    key={productItem?.product_id}
                    productItem={productItem}
                    index={index}
                    sliderIndex={sliderIndex}
                    productsLength={productToShow.length}
                    overrideImage={overrideImage}
                    storeChain={storeChain}
                    userContext={userContext}
                    storeLocatorConfig={storeLocatorConfig}
                    mapProducts={mapContext.products}
                  />
                ))}
              </Slider>
            ) : (
              <Slider slidesToShow={1}>
                {productToShow.map((productItem, index) => (
                  <SliderCard
                    key={productItem?.product_id}
                    productItem={productItem}
                    index={sliderIndex}
                    sliderIndex={sliderIndex}
                    productsLength={productToShow.length}
                    overrideImage={overrideImage}
                    storeChain={storeChain}
                    userContext={userContext}
                    storeLocatorConfig={storeLocatorConfig}
                    mapProducts={mapContext.products}
                  />
                ))}
              </Slider>
            ))}
        </>
      );
    } else if (!!overrideImage) {
      return (
        <Stack direction="column" alignItems="center">
          <img
            alt="product"
            src={overrideImage}
            width={150}
            style={{ maxHeight: 250, objectFit: "contain" }}
          />
        </Stack>
      );
    } else {
      return <></>;
    }
  },
);

const ProductSection = memo(({ products, theme, storeChain, storeState }) => {
  const mapContext = useContext(MapContext);
  const userContext = useContext(UserContext);
  const storeLocatorConfig = userContext?.state?.config;
  const productLabel =
    userContext?.state?.orgProperties?.productNameReplacement || "Product";
  const productsToShow = useMemo(
    () =>
      products?.filter(
        (productItem) =>
          !!mapContext.products?.find((p) => p.id === productItem?.product_id),
      ),
    [products, mapContext.products],
  );
  const getDefaultImage = () => {
    if (storeLocatorConfig.storeCardDefaultImageStates?.[storeState]) {
      return storeLocatorConfig.storeCardDefaultImageStates[storeState];
    }
    return storeLocatorConfig.storeCardDefaultImage;
  };
  return (
    <>
      {storeLocatorConfig?.storeDetails?.productsAvailable &&
        !!productsToShow?.length && (
          <div style={{ marginTop: 10 }}>
            {!storeLocatorConfig?.hideProductsLabel && (
              <Typography variant="subtitle2">
                {productLabel}s sold at this store:
              </Typography>
            )}
            {!storeLocatorConfig?.hideAvailabilityMessage && (
              <Typography variant="subtitle4" color="textSecondary">
                {storeLocatorConfig?.customAvailabilityMessage ||
                  "Please check availability with the store. Inventory may vary."}
              </Typography>
            )}

            <Products
              products={productsToShow}
              classes={theme}
              storeLocatorConfig={storeLocatorConfig}
              userContext={userContext}
              storeChain={storeChain}
            />
          </div>
        )}
      {(storeLocatorConfig?.storeDetails?.productsAvailable === false ||
        !productsToShow?.length) && (
        <div style={{ marginTop: 10 }}>
          {!storeLocatorConfig?.hideAvailabilityMessage && (
            <Typography variant="subtitle2" textAlign="center">
              {storeLocatorConfig?.customAvailabilityMessage ||
                `Check store for ${productLabel} availability.`}
            </Typography>
          )}
          {getDefaultImage() && (
            <Products
              userContext={userContext}
              overrideImage={getDefaultImage()}
              storeLocatorConfig={storeLocatorConfig}
              storeChain={storeChain}
            />
          )}
        </div>
      )}
    </>
  );
});

class StoreDetailsComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      storeProducts: [],
      deliveryEntities: [],
    };
  }

  setProducts() {
    const { result } = this.props;
    const reduceProducts = (acc, product) => {
      if (product && !acc.find((ac) => ac.product_id === product.product_id)) {
        acc.push(product);
      }
      return acc;
    };
    if (result?.products?.length) {
      this.setState({
        storeProducts: result.products?.reduce(reduceProducts, []),
      });
    } else {
      getProductsForStores([result?.id]).then((response) => {
        this.setState({
          storeProducts: response?.reduce(reduceProducts, []) || [],
        });
      });
    }
  }

  setDeliveryEntities() {
    const { result } = this.props;
    if (result?.delivery_entities?.length) {
      this.setState({ deliveryEntities: result.delivery_entities });
    } else {
      getDeliveryEntitiesForStores([result?.id]).then((response) => {
        this.setState({ deliveryEntities: response || [] });
      });
    }
  }

  componentDidMount() {
    this.setProducts();
    this.setDeliveryEntities();
  }

  componentDidUpdate(prevProps) {
    if (this.props.result?.id !== prevProps.result?.id) {
      this.setState({ storeProducts: [], deliveryEntities: [] }, () => {
        this.setProducts();
        this.setDeliveryEntities();
      });
    } else if (!this.props.result?.id) {
      this.setState({
        storeProducts: [],
        deliveryEntities: [],
      });
    }
  }

  stripAddress() {
    const { address, city, state, zipcode } = this.props.result;
    const addressRegex = new RegExp(
      `,|\\b${city}\\b,|\\b${state}\\b|\\b${zipcode}\\b|\\usa?\\b`,
      "gi",
    );
    return address
      .replace(/,(\S)/g, ", $1")
      .replace(addressRegex, "")
      .replace(/\s+/g, " ")
      .trim();
  }
  render() {
    const storeLocatorConfig =
      (this.context || this.props.context)?.state?.config ||
      this.props.storeLocatorConfig;
    const orgProperties =
      (this.context || this.props.context)?.state?.orgProperties ||
      this.props.orgProperties;

    const _products = _.memoize(
      (storeProducts, productPriority) => {
        let priorityProducts =
          productPriority
            ?.map((id) =>
              (storeProducts || []).find((p) => p.product_id === id),
            )
            .filter((p) => !!p) || [];
        priorityProducts = [
          ...priorityProducts.slice(0, 2).reverse(),
          ...priorityProducts.slice(2),
        ];
        return [
          ...priorityProducts,
          ...(storeProducts || []).filter(
            (p) => !productPriority?.includes(p.product_id),
          ),
        ];
      },
      (storeProducts, productPriority) =>
        JSON.stringify([storeProducts, productPriority]),
    )(this.state.storeProducts, storeLocatorConfig?.productPriority);
    const featuredContent =
      !!this.props.result?.featured_content &&
      (typeof this.props.result?.featured_content === "string"
        ? JSON.parse(this.props.result?.featured_content)
        : this.props.result?.featured_content || []
      ).filter((fc) => {
        if (fc.startDate || fc.endDate || fc.recurringRule) {
          const todayValid = validateSchedule(
            fc.startDate,
            fc.endDate,
            fc.recurringRule,
          );
          return todayValid;
        }
        return true;
      });
    const startDateDateTime = DateTime.fromSQL(this.props.result.start_date);
    const endDateDateTime = DateTime.fromSQL(this.props.result.end_date);
    const todayValid = validateSchedule(
      this.props.result.start_date,
      this.props.result.end_date,
      this.props.result.recurrence_rule,
    );
    return (
      <div>
        <Stack direction="row">
          <Stack direction="column" flex={1}>
            <Typography
              variant="body2"
              style={{
                lineHeight: "1.5em",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: "260px",
              }}
            >
              {(this.context || this.props.context)?.formatText(
                getStoreName(
                  this.props.result,
                  storeLocatorConfig,
                  orgProperties?.noChainNameReplacement,
                ),
              )}

              {(this.context || this.props.context)?.checkAdminPermission(
                RESOURCES.STORES_UPDATE,
              ) && (
                <Tooltip title="Edit store">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      window.parent.postMessage(
                        {
                          action: "edit-store",
                          data: this.props.result,
                        },
                        "*",
                      );
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            {storeLocatorConfig.storeDetails?.showType && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{
                  lineHeight: "1.5em",
                  overflow: "hidden",
                }}
              >
                {this.props.result.type}
              </Typography>
            )}
            {storeLocatorConfig?.storeDetails?.showReviews && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <ReactStars
                  count={5}
                  edit={false}
                  value={
                    this.props.result?.reviews?.length
                      ? round(
                          mean(_.map(this.props.result?.reviews, "rating")),
                          1,
                        )
                      : 0
                  }
                />
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  style={{ marginLeft: 5 }}
                >
                  ({this.props.result?.reviews?.length || 0})
                </Typography>
              </div>
            )}
            {storeLocatorConfig?.storeDetails?.showAddress && (
              <>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{
                    lineHeight: "1.5em",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: "260px",
                  }}
                >
                  {this.stripAddress()}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{
                    lineHeight: "1.5em",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {[this.props.result.city, this.props.result.state]
                    .filter((c) => !!c)
                    .join(", ")}{" "}
                  {this.props.result.zipcode || ""}
                </Typography>
              </>
            )}
            {!!storeLocatorConfig?.storeDetails?.contactInfo && (
              <ContactInfo
                item={this.props.result}
                deliveryEntities={this.state.deliveryEntities}
              />
            )}
          </Stack>
          <StoreActionButtons
            item={this.props.result}
            show={["buyOnlineLink", "openInfo", "directionsButton"]}
            filters={this.props.filters}
          />
        </Stack>
        <Typography variant="caption">
          {storeLocatorConfig.storeDetails?.showReviews &&
            !!storeLocatorConfig?.storeDetails?.numberOfReviewsToShow && (
              <Typography
                variant="caption"
                style={{
                  display: "block",
                  lineHeight: "1.5em",
                  overflow: "hidden",
                }}
              >
                {this.props.result.reviews?.[0]?.title || ""}
              </Typography>
            )}
          {storeLocatorConfig?.storeDetails?.showDescription &&
            this.props.result.description && (
              <Typography variant="caption">
                {this.props.result.description || ""}
              </Typography>
            )}

          {!!(this.props.result.start_date && this.props.result.end_date) && (
            <>
              <Divider variant="middle" />
              <Stack
                direction="column"
                spacing={1}
                padding={1}
                width="100%"
                alignItems="center"
                style={{
                  lineHeight: "1.5em",
                  textAlign: "center",
                }}
              >
                {todayValid && (
                  <Typography variant="caption" color="green">
                    Now available
                  </Typography>
                )}
                {!todayValid && (
                  <Typography variant="caption">
                    {`Please verify availability with location`}
                  </Typography>
                )}
                {DateTime.now() < startDateDateTime && (
                  <Typography variant="caption">
                    {`Starting ${startDateDateTime.toRelativeCalendar()}`}
                  </Typography>
                )}
                {DateTime.now() > endDateDateTime &&
                  !endDateDateTime.hasSame(DateTime.now(), "day") && (
                    <Typography variant="caption">
                      {`Ended ${endDateDateTime.toRelativeCalendar()}`}
                    </Typography>
                  )}
                {!this.props.result.recurrence_rule &&
                  startDateDateTime < DateTime.now() &&
                  DateTime.now() < endDateDateTime && (
                    <Typography variant="caption">
                      {`You can find us at this location until ${endDateDateTime.toLocaleString(
                        DateTime.DATE_MED_WITH_WEEKDAY,
                      )}`}
                    </Typography>
                  )}
                {!!this.props.result.recurrence_rule && (
                  <>
                    <Typography variant="caption">
                      {`You ${
                        DateTime.now() > endDateDateTime &&
                        !endDateDateTime.hasSame(DateTime.now(), "day")
                          ? "could"
                          : "can"
                      } find us at this location ${rrulestr(
                        this.props.result.recurrence_rule,
                      ).toText()}`}
                    </Typography>
                  </>
                )}
                <Typography variant="caption">
                  <Chip
                    size="small"
                    label={startDateDateTime.toLocaleString(
                      DateTime.DATE_MED_WITH_WEEKDAY,
                    )}
                  />{" "}
                  to{" "}
                  <Chip
                    size="small"
                    label={endDateDateTime.toLocaleString(
                      DateTime.DATE_MED_WITH_WEEKDAY,
                    )}
                  />
                </Typography>
              </Stack>
            </>
          )}
          {!storeLocatorConfig?.hideFeaturedContent &&
            !!featuredContent?.length && (
              <>
                <Divider variant="middle" />
                <Stack
                  direction="row"
                  spacing={5}
                  width="100%"
                  style={{ overflowX: "auto" }}
                  alignItems="center"
                  justifyContent={
                    featuredContent?.length === 1 ? "center" : "flex-start"
                  }
                >
                  {featuredContent?.map((s) => (
                    <Paper
                      className="featured-content"
                      id={`${s.name}`}
                      style={{
                        marginTop: 10,
                        marginBottom: 10,
                        width: 300,
                        minWidth: 300,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        cursor: s.link ? "pointer" : undefined,
                      }}
                      onClick={() => {
                        TagManager.dataLayer({
                          dataLayer: {
                            event: "featuredContentClick",
                            category: s.name,
                            label: this.props.result.name,
                            data: this.props.result.id,
                            city: this.props.result.city,
                          },
                        });
                        if (s.link) {
                          window.open(s.link);
                        }
                      }}
                    >
                      {!!s.image && (
                        <Box width="100%" height={s.imageHeight || "200px"}>
                          <img
                            height="100%"
                            width="100%"
                            src={s.image}
                            style={{ objectFit: s.imageFit || "cover" }}
                            alt="featured content"
                          />
                        </Box>
                      )}
                      {!![s.title, s.message, s.callToAction].filter((c) => !!c)
                        .length && (
                        <Stack direction="column" alignItems="flex-start" p={1}>
                          {!!s.title && (
                            <Typography
                              style={{
                                fontWeight: "bold",
                                fontSize: 18,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                              }}
                            >
                              {s.title}
                            </Typography>
                          )}
                          {!!s.message && (
                            <Typography
                              style={{
                                fontSize: 14,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                                maxHeight: "4.5em",
                              }}
                              noOfLines={3}
                            >
                              {s.message}
                            </Typography>
                          )}
                          {!!s.callToAction && (
                            <Button
                              sx={{
                                maxWidth: "100%",
                                overflow: "hidden",
                              }}
                              variant="contained"
                            >
                              {s.callToAction}
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          {!!storeLocatorConfig.storeDetails?.additionalServices?.length && (
            <>
              <Divider variant="middle" />
              <Stack
                direction="row"
                spacing={5}
                width="100%"
                style={{ overflowX: "auto" }}
                alignItems="flex-start"
                justifyContent="center"
              >
                {storeLocatorConfig.storeDetails.additionalServices.map((s) => (
                  <Stack direction="row" alignItems="center">
                    {!!s.icon && (
                      <img
                        alt="additional service icon"
                        src={s.icon}
                        style={{
                          objectFit: "cover",
                          borderRadius: 10,
                          width: "1rem",
                          height: "1rem",
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      textAlign="center"
                      margin={0}
                      marginTop={0}
                    >
                      {s.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
          <Divider variant="middle" />

          <ProductSection
            products={_products}
            theme={this.props.theme}
            storeChain={this.props.result.chain}
            storeState={this.props.result.state}
          />
        </Typography>
      </div>
    );
  }
}

StoreDetailsComponent.propTypes = {
  result: PropTypes.object.isRequired,
};
StoreDetailsComponent.contextType = UserContext;

export default withTheme(StoreDetailsComponent);
